<template>
    <div>
        <div class="fixed white" style="width: 100%;" id="tool-bar">
        <v-row class="ma-0 pa-3" align="center">
            <v-col md="auto" cols="12">
                <slot name="breadcrumbs" :item="true"></slot>
            </v-col>
            <v-col lg="auto" md="12" class="flex-grow-1">
                <v-row class="border">
                    <!-- section -->
                    <v-col sm="auto" cols="auto" class="d-flex flex-row justify-start">
                        <bee-bond-up-down
                            v-model="journalNo"
                            @keydown-enter-prevent="fetchJournalByNo()"
                            @programmatically-changed="fetchJournalByNo()"
                            :items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? materials_journals.map(c => c.sub_journal_no) : materials_journals.map(c=>c.no)"
                            class="medium-placeholder"
                            :label="$t('MaterialsJournalPage.journal-number-automatic-or-manual')"
                        ></bee-bond-up-down>
                    </v-col>
                    <!-- warehouse -->
                    <v-col md="3" sm="auto" cols="12" class="d-flex flex-column justify-center">
                        <bee-autocomplete 
                            v-model="default_warehouse_id" 
                            :disabled="journal.is_posted || file_is_locked" 
                            context="materials_journal"
                            :items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
                            item-text="name"
                            :item-value="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id'"
                            :label="$t('MaterialsJournalPage.warehouse')"
                            @change="changeWarehouse()"
                            :hide-details="true"
                            :action-name="actions.GET"
                        ></bee-autocomplete>
                    </v-col>
                    <!-- add button -->
                    <v-col sm="auto" cols="12" class="d-flex flex-column px-lg-0 justify-center">
                        <bee-btn  
                            @click="addEntryDialog = true"
                            :content="$t('MaterialsJournalPage.add')"
                            color="addingColor"
                            context="materials_journal"
                            icon="fa-plus"
                            :action-name="actions.CREATE"
                            :disabled="file_is_locked"
                        ></bee-btn>
                    </v-col>
                    <v-col v-if="!journal.invoice_id && !journal.manufacturing_file_id && !journal.transaction_bond_id" cols="12" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex flex-column justify-center">
                        <bee-btn
                            @click="deleteJournalDialog = true"
                            :content="$t('MaterialsJournalPage.delete')"
                            color="deletingColor"
                            context="materials_journal"
                            icon="fa-trash"
                            :action-name="actions.DELETE"
                            :disabled="file_is_locked"
                        ></bee-btn>
                    </v-col>
                    <v-spacer/>

                    <!-- section -->
                    <v-col lg="auto" sm='auto' cols="12" class="d-flex justify-center flex-column ps-lg-0 py-lg-0">
                        <bee-btn
                            v-if="!journal.transaction_bond_id"
                            context="materials_journal"
                            :action-name="actions.EDIT"
                            :content="$vuetify.breakpoint.lgAndUp 
                                ? journal.is_posted
                                    ? journal.invoice_id 
                                        ? $t('MaterialsJournalPage.view-invoice')
                                        : journal.manufacturing_file_id ? $t('MaterialsJournalPage.view-manufacturing-bond') : $t('MaterialsJournalPage.edit')
                                    : $t('MaterialsJournalPage.save')
                                : ''
                            " 
                            :icon="journal.is_posted ? (journal.invoice_id || journal.manufacturing_file_id ? 'far fa-share-square fa-rotate-180' : 'fa-envelope-open') : 'fa-envelope'" 
                            :outer-class="$vuetify.breakpoint.lgAndUp ? 'py-8': ''"
                            :disabled="file_is_locked && !journal.invoice_id && !journal.manufacturing_file_id"
                            :color="journal.is_posted ? 'deletingColor' : 'blueGray'"
                            @click="journal.is_posted ? ( journal.invoice_id ? reForwardToRelatedInvoice() : journal.manufacturing_file_id ? reForwardToRelatedManufacturingFile() : unPost()) : post()">
                        >
                        </bee-btn>
                    </v-col>
                    <v-col md="1" sm="12" 
                        :class="[
                            journal.is_posted ? 'postedColor': 'unpostedColor',
                            $vuetify.breakpoint.mdAndUp ? 'border-round-end': 'border-round-bottom'
                        ]"
                        class="d-flex justify-center align-center white--text text--title text-center text-wrap"
                    >
                        <span class="text-center align-center" style="line-height: 1.2;">
                            {{journal.is_posted ? '' : $t('MaterialsJournalPage.not')}}
                            <br v-if="!journal.is_posted && $vuetify.breakpoint.mdAndUp">
                            {{$t('MaterialsJournalPage.posted')}}
                        </span>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        </div>
        <!-- table -->
        <div class="ma-auto absolute" id="main" style="width: 100%; height: auto">
        <v-card flat>
            <v-card-text class="pt-0 px-3">
                <bee-smart-table
                    v-model="tableInputs"
                    max-height="65vh"
                    mode="materials-journals"
                    context="materials_journal"
                    :action-name="actions.CREATE"
                    ref="table" 
                    :with-color="false"
                    :zebra="true"
                    empty-state="dashes"
                    :hide-footer="true"
                    :addable="!journal.is_posted"
                    :editable="!journal.is_posted" 
                    :configs="configs"
                    :headers="headers"
                    :items="items || []"
                    :show-menu-actions="true"
                    highlighted-color="blueGrayLighten3"
                    :highlighted-expression="(item)=>{ return item.id == parseInt($route.query.entryId) }" 
                    @cell-change="items[$event.index][$event.header.value] = $event.value"
                    @reset-data="fetchEntry($event)"
                    @row-edited="updateEntry($event)"
                    @row-added="addEntry($event)"
                    @delete-item="deleteEntry($event)"
                    @material-statement="forwardToMaterialStatement($event)"
                    :right-click-menu-items="actionsList"
                    :messagesSelectRowValidation="messagesSelectRowValidation"
                    @nblur="handlBlurJournalTable"
                ></bee-smart-table>
            </v-card-text>
        </v-card>
        <!-- Add dialog -->
        <v-dialog v-model="addEntryDialog" max-width="500" transition="dialog-top-transition">
            <v-card>
                <v-card-title class="headline">{{$t('MaterialsJournalPage.add-journal')}}</v-card-title>
                <v-card-text>
                    <bee-date-picker
                      context="materials_journal"
                      :action-name="actions.CREATE"
                      :label="$t('MaterialsJournalPage.journal-date')"
                      v-model="date"
                      :rules="[rules.date]"
                    ></bee-date-picker>

                    <v-autocomplete 
                        v-if="multipleWarehouses"
                        v-model="warehouseId"
                        :items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
                        item-text="name"
                        :item-value="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id'"
                        outlined
                        dense
                        :label="$t('inputs.warehouse')"
                    />
                </v-card-text>
                <v-card-actions class="ms-3">
                    <v-btn color="greenDarken1" text @click="addMaterialJournal" :loading="loading" >
                        {{$t('MaterialsJournalPage.create')}}
                    </v-btn>
                    <v-btn color="redDarken1" text @click="addEntryDialog = false" >
                        {{$t('MaterialsJournalPage.cancel')}}
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- delete dialog -->
        <bee-dialog
            @handler="confirmDeleteEntry(); $emit('close')"
            @close="deleteEntryDialog = false"
            v-model="deleteEntryDialog"
            :title="$t('MaterialsJournalPage.delete-entry')"
            :message="$t('MaterialsJournalPage.are-you-sure-you-want-to-delete-the-entry')"
        ></bee-dialog>
        <bee-dialog
            @handler="deleteJournal()"
            @close="deleteJournalDialog = false"
            v-model="deleteJournalDialog"
            :title="$t('MaterialsJournalPage.delete-journal')"
            :message="$t('MaterialsJournalPage.are-you-sure-you-want-to-delete-the-journal')"
        ></bee-dialog>
        </div>
    </div>
</template>

<script>
import rules from "../../../validation";
import actions from "../../../permissions/actions-names.js";
import store from "../../../store/index.js";
import EventBus from '../../../event-bus';
import { entryDetectedValidation } from './helper/validation-detected-material-entry';
import { mapState } from 'vuex';
import { refactorMaterialEntry } from "./helper/operations-calculated-properties.js";
import { userType } from '@/helpers/enums'
import { multipleWarehouses, isMangeUnitAndRegionAndPatchAndLot } from '@/configs/global'

export default {
    data: () => ({
        ...rules,
        actions,
        loading: false,
        default_warehouse_id: null,
        journalNo: null,
        selected_warehouse_id: null,
        deleteEntryDialog: false,
        addEntryDialog: false,
        deleteJournalDialog: false,
        date: new Date().toISOString().substr(0, 10),
        default_journal: {
            is_posted: false
        },
        selected_entry: null,
        messagesSelectRowValidation: [],
        linkedJournalRules:{ in_quantity: true, out_quantity: true, count: true, material_id: true, price: true },
        userType,
        multipleWarehouses,
        tableInputs: {},
        warehouseId: null,
        isMangeUnitAndRegionAndPatchAndLot
    }),

    methods:{
        fetchJournal(id){
            this.$refs.table.openLoading();
            store.dispatch("materialsJournals/fetch", { journalId: id })
            .then(response=>{
                // userTypePermission === userType.warehouseKeeper && multipleWarehouses ? materials_journals.map(c=>c.sub_journal_no).filter(c => c !== null) : materials_journals.map(c=>c.no)
                if(this.userTypePermission === this.userType.warehouseKeeper && this.multipleWarehouses) {
                    this.journalNo = response.sub_no
                } else {
                    this.journalNo = response.no;
                }
                this.changeWarehouse();
                this.$refs.table.endLineInputValues = { date: new Date().toISOString(), warehouse_id: this.selected_warehouse_id};
                this.$refs.table.onEsc();
                this.$refs.table.closeLoading();
                this.$router.replace({name: 'materials-journal', params: { id }, query: this.$route.query}).catch(()=>{})
            })
        },
        // fetchJournalByNo(){
        //     var materialJournal = this.materials_journals?.find(c => c.no == this.journalNo)
        //     if(materialJournal.id != Number(this.$route.params.id))
        //         this.fetchJournal(materialJournal.id)
        // },
        fetchJournalByNo(){
            var materialJournal = null
            if (this.userTypePermission === userType.warehouseKeeper && this.multipleWarehouses) {
                materialJournal = this.materials_journals?.find(c => c.sub_journal_no == this.journalNo)
                this.materials_journals.map( c => c.sub_journal_no) 
            } else {
                materialJournal = this.materials_journals?.find(c => c.no == this.journalNo)
                this.materials_journals.map(c=>c.no)
            }
            if(materialJournal.id != Number(this.$route.params.id))
                this.fetchJournal(materialJournal.id)
        },
        deleteJournal(){
            if(this.journal.id)
                store.dispatch("materialsJournals/delete",{ journalId: this.journal.id })
                    .then(()=> 
                    {
                        store.dispatch("materialsJournals/fetchAll", { 
                            fromDate: this.currentFinancialFiles.start_date, 
                            toDate: this.date,
                            warehouseId: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? this.warehousesForKeeper[0].warehouse_id : null
                        })
                            .finally(()=> this.fetchLastjournal())
                        
                    })
        },
        fetchLastjournal(){
            if(this.materials_journals.length > 0)
                this.fetchJournal(this.materials_journals[this.materials_journals.length -1].id)
            
            else{
                store.dispatch("materialsJournals/clear")
                this.journalNo = null
                this.$router.replace({name: 'materials-journals'})
            }
        },
        addMaterialJournal(){
            this.loading = true

            store.dispatch("materialsJournals/create",{ date: this.date,  warehouse_id: this.warehouseId })
            .then(response => { 
                this.$router.replace({name: 'materials-journal', params: { id: response.id }}).catch(()=>{})
                if (this.userTypePermission === userType.warehouseKeeper && this.multipleWarehouses) { 
                    this.journalNo = response.sub_no;
                } else {
                    this.journalNo = response.no
                }
                this.$refs.table.endLineInputValues = { date: new Date().toISOString(), warehouse_id: this.warehouse_id }, 
                this.$refs.table.onEsc();

                const fromDate = this.currentFinancialFiles.start_date;
                const toDate = new Date().toISOString().substr(0, 10);
                store.dispatch("materialsJournals/fetchAll", { fromDate, toDate, warehouseId: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? this.warehousesForKeeper[0].warehouse_id : null })
            })
            .finally(() => this.reSet())
        },
        reSet(){
            this.loading = false;
            this.addEntryDialog = false
            this.date = new Date().toISOString().substr(0, 10).toString();
            this.selected_entry = null
            this.deleteEntryDialog = false
        },
        changeWarehouse(){
            EventBus.$emit('initialize-last-row', { warehouse_id: this.default_warehouse_id });
            this.selected_warehouse_id= this.default_warehouse_id;
        },

        unPost(){
            if(this.journal.id)
                store.dispatch("materialsJournals/changeToNotPosted", { journalId: this.journal.id })
                .then(()=> this.fetchJournal(this.journal.id))
        },
        post(){
            if(this.journal.id)
                store.dispatch("materialsJournals/changeToPosted", { journalId: this.journal.id })
                .then(()=> this.fetchJournal(this.journal.id))
        },
        updateEntry(material_entry){ 
            let unit_size_id = this.materials.find(c=>c.id == this.items[material_entry.rowIndex].material_id).unit_size_id    
            let entry = refactorMaterialEntry(this.items[material_entry.rowIndex], this.configs.financial_file.default_currency_id, unit_size_id)
            
            entry.materials_journal_id = this.journal.id
            delete entry.in_quantity
            delete entry.out_quantity
            if(this.$refs.table.$refs.table.$refs.table.valids[`valid_${material_entry.rowIndex}`])
                store.dispatch("materialsJournals/updateEntry", {
                    journalId: this.journal.id,
                    material_entry: entry,
                })
                .then(() => { 
                    material_entry.deleteRowIndex()
                })
                .finally(()=>{
                    material_entry.connectionEnded(1, "BeeAutocomplete")
                })
            else
                EventBus.$emit('error', this.$t('make-sure-the-data-entered-is-correct'))
        },
        deleteEntry($event){
            this.selected_entry = {...$event}
            this.deleteEntryDialog = true
        },
        confirmDeleteEntry(){
            store.dispatch("materialsJournals/deleteEntry", { journalId: this.journal.id, entryId: this.selected_entry.id })
            .then(()=>{
                this.reSet()
            })
        },
        fetchEntry(entry){
            store.dispatch("materialsJournals/fetchEntry", entry.id)
        },
        forwardToMaterialStatement(entry){
            this.$router.push({ name: 'material-statment', params: { id: entry.material_id }, query: { invoiceId: this.$route.params.id } })
        },
        addEntry(material_entry){
            let unit_size_id = this.materials.find(c=>c.id == material_entry.value.material_id).unit_size_id
            let entry = refactorMaterialEntry(material_entry.value, this.configs.financial_file.default_currency_id, unit_size_id)
            const entryDate = 
                entry.date || (
                    this.items.length
                        ? this.items[this.items.length - 1].date.slice(0, 10)
                        : new Date().toISOString().slice(0, 10)
                );

            entry.materials_journal_id = this.journal.id
            delete entry.in_quantity
            delete entry.out_quantity
            entry.date = entryDate;
            store.dispatch('materialsJournals/createEntry', {
                journalId: this.journal.id,
                material_entry: entry,
            })
            .then(() => {
                material_entry.deleteRowData({
                    warehouse_id: this.selected_warehouse_id,
                    date: entryDate,
                })
            })
            .finally(()=>  material_entry.connectionEnded(1, "BeeNumericField"))
        },

        handlBlurJournalTable(entry){
            this.messagesSelectRowValidation = entryDetectedValidation(entry, this.linkedJournalRules);
        },
        reForwardToRelatedInvoice(){
            store.dispatch("invoices/fetchInvoiceType", { invoiceId: this.journal.invoice_id}).then(response => {
                if(response == 'Buy')
                    this.$router.push({name: 'invoices-buy', params:{ id: this.journal.invoice_id }})
                else if (response == 'Sale')
                    this.$router.push({name: 'invoices-sale', params:{ id: this.journal.invoice_id }})
            })
        },
        reForwardToRelatedManufacturingFile(){
            this.$router.push({name: 'manufacturing-bond', query: { id: this.journal.manufacturing_file_id }})
        }
    },

    computed:{
        ...mapState({
            ...mapState("materials", ["materials"]),
            ...mapState("configs", ["configs"]),
            ...mapState("materialsJournals", ["materials_journals"]),
            ...mapState("materialsJournals", ["materials_journal"]),
            ...mapState("warehouses", ["warehouses"]),
            userTypePermission: state => state.permissions.user_type,
            warehousesForKeeper: state => state.warehouses.warehousesForKeeper,
            currentFinancialFiles: state => state.financialFiles.current_financial_file
        }),
        journal(){
            return Object.keys(this.materials_journal).length === 0 ? this.default_journal : this.materials_journal  
        },
        items(){
            return this.materials_journal?.entries?.map(obj=> ( {
                    ...obj, 
                    in_quantity: obj.type == 1 ? obj.quantity.quantity : null,
                    out_quantity: obj.type == 2 ? obj.quantity.quantity : null,
                    warehouse_id: this.materials.find(c=>c.id == obj.material_id).warehouse_id
                })) 
        },
        warehouseMaterials(){
            return this.materials
            .filter(c => this.selected_warehouse_id ? c.warehouse_id == this.selected_warehouse_id : true)
            .filter(c => c.type == 2)
        },
        file_is_locked(){
            return this.configs?.financial_file?.is_locked 
        },
        on_mdAndUp(){
            return this.$vuetify.breakpoint.mdAndUp
        },
        headers(){
            const headers = [
                {
                    text: this.$t('MaterialsJournalPage.headers.no'),
                    value: 'no',
                    type: 'string',
                    width: '5%',
                    required: true,
                    editable: false 
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.in-quantity'),
                    value: 'in_quantity',
                    type: 'numeric',
                    width: '10%',
                    required: true,
                    editable: true, rules: [this.linkedJournalRules.in_quantity] 
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.out-quantity'),
                    value: 'out_quantity',
                    type: 'numeric',
                    width: '10%',
                    required: true,
                    editable: true, rules: [this.linkedJournalRules.out_quantity] 
                },
              
                {
                    text: this.$t('MaterialsJournalPage.headers.material'),
                    value: 'material_id',
                    type: 'materials-autocomplete',
                    width: '10%',
                    required: true,
                    editable: true, rules:[this.linkedJournalRules.material_id],
                    // this.materials.filter(c => c.warehouse_id === this.tableInputs.warehouse_id && c.type === 2) : this.materials.filter(c => c.type === 2)
                    settings: { 
                        items: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? this.materials.filter(c => c.warehouse_id === this.default_warehouse_id && c.type === 2) : this.materials.filter(c => c.type === 2), 
                        subItems: this.warehouseMaterials, context: 'materials', actionName: this.actions.GET }
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.count'),
                    value: 'count',
                    type: 'numeric',
                    width: '5%',
                    editable: true
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.price'),
                    value: 'price',
                    type: 'money',
                    width: '10%',
                    required: true,
                    editable: true, rules: [this.rules.money && this.linkedJournalRules.price]
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.notes'),
                    value: 'notes',
                    type: 'string',
                    width: '10%',
                    required: true,
                    editable: true
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.document'),
                    value: 'doc_no',
                    type: 'string',
                    width: '5%',
                    required: true,
                    editable: true
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.account'),
                    value: 'account_id',
                    type: 'accounts-autocomplete',
                    width: '15%',
                    required: true,
                    editable: true, settings: { itemText: 'name' }
                },
                {
                    text: this.$t('MaterialsJournalPage.headers.date'),
                    value: 'date',
                    type: 'date',
                    width: '10%',
                    required: true,
                    editable: true
                },
                { text: '', value: 'hint', type: 'hint', width: '2%', editable: false,},
            ]
            if(isMangeUnitAndRegionAndPatchAndLot) {
                headers.splice(10,0, 
                {
                    text: 'رقم الباتش',
                    name: 'patchNumber',
                    value: 'patchNumber',
                    type: 'string',
                    editable: true,
                    settings: { context: 'invoice', actionName: actions.GET },
                    width: '10%'
                },
                {
                    text: 'رقم اللوت',
                    name: 'lotNumber',
                    value: 'lotNumber',
                    type: 'string',
                    editable: true,
                    settings: { context: 'invoice', actionName: actions.GET },
                    width: '10%'
                },
                {
                    text: 'تاريخ الانتهاء',
                    name: 'endDate',
                    value: 'endDate',
                    type: 'date',
                    editable: true,
                    settings: { context: 'invoice', actionName: actions.GET },
                    width: '13%'
                })
            }
            if((multipleWarehouses && this.userTypePermission !== userType.warehouseKeeper) || (!multipleWarehouses)) {
                headers.splice(3, 0,
                    {
                        text: this.$t('MaterialsJournalPage.headers.warehouse'),
                        value: 'warehouse_id',
                        type: 'autocomplete',
                        width: '10%',
                        required: true,
                        editable: true ,
                        settings: { itemText: 'name', items: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? this.warehousesForKeeper : this.warehouses, clearable: true, itemValue: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id', context: 'warehouses', actionName: actions.GET }
                    },
                )
            }
            return headers
        },
        actionsList(){
            return [
                {
                    name: 'material-statement',
                    disabled: false,
                    action_name: actions.GET,
                    text: this.$t('MaterialsJournalPage.context-menu.material-statement')
                },
                {
                    name: 'edit',
                    disabled: this.journal.invoice_id || this.journal.manufacturing_file_id || this.journal.is_posted,
                    action_name: actions.EDIT,
                    text: this.$t('MaterialsJournalPage.context-menu.edit')
                }, 
                {
                    name: 'delete-item',
                    disabled: this.journal.invoice_id || this.journal.manufacturing_file_id || this.journal.is_posted,
                    action_name: actions.DELETE,
                    text: this.$t('MaterialsJournalPage.context-menu.delete-item')
                },
            ]
        },
    },
    mounted(){
        window.observer.observe(document.querySelector("#tool-bar"))
        
        EventBus.$on('warehouse-changed', (warehouse_id, material_id = null)=>{ 
            if(!warehouse_id && material_id)
            {
                // تحديث قائمة مواد المستودع
                this.selected_warehouse_id = this.materials.find(c=>c.id == material_id)?.warehouse_id
                // تعيين المستودع التابع للمادة
                EventBus.$emit('initialize-last-row', { warehouse_id: this.selected_warehouse_id })
            }    
            else
                this.selected_warehouse_id =  warehouse_id
        });
        if(this.userTypePermission === userType.warehouseKeeper && this.multipleWarehouses) {
            this.default_warehouse_id = this.warehousesForKeeper[0].warehouse_id
            this.selected_warehouse_id = this.default_warehouse_id
        } else {
            this.default_warehouse_id = this.configs.warehouse.id
            this.selected_warehouse_id = this.default_warehouse_id
        }
        EventBus.$emit('initialize-last-row', { warehouse_id: this.selected_warehouse_id })
    },

    async beforeRouteEnter(to, from, next) {
        let promises = []

        let configs = store.state.configs.configs;
        if(Object.keys(configs).length == 0)
            promises.push(store.dispatch("configs/fetchAll"))

        // let materials = store.state.materials.materials;
        //if(Object.keys(materials).length == 0)
            promises.push(store.dispatch("materials/fetchAll"))

        let warehouses = store.state.warehouses.warehouses;
        if(Object.keys(warehouses).length == 0)
            promises.push(store.dispatch("warehouses/fetchAll"))

        await Promise.all(promises).then(async ()=>{
            console.log(store.state.warehouses.warehousesForKeeper)
            let financialFile = store.state.financialFiles.current_financial_file;
            let fromDate = financialFile.start_date;
            let toDate = new Date().toISOString().substr(0, 10);
            await store.dispatch("materialsJournals/fetchAll", { 
                fromDate, 
                toDate,
                warehouseId: store.state.permissions.user_type === userType.warehouseKeeper && multipleWarehouses ? store.state.warehouses.warehousesForKeeper.length ? store.state.warehouses.warehousesForKeeper[0].warehouse_id : null : null
            })
            .then(() => {
                next((vm) => {
                    const materialsJournals = store.state.materialsJournals.materials_journals;
                    let id = null;
                    if(to.params.id){
                        id = to.params.id;
                    }
                    else{
                        id = materialsJournals[materialsJournals.length -1]?.id;
                    }
                    if (id) {
                        store.dispatch("materialsJournals/fetch", { journalId: id })
                        .then(response => {
                            if (vm.userTypePermission === userType.warehouseKeeper && vm.multipleWarehouses) { 
                                vm.journalNo = response.sub_no;
                                vm.$router.replace({name: 'materials-journal', params: { id }, query: to.query})
                            } else {
                                vm.journalNo = response.no
                                vm.$router.replace({name: 'materials-journal', params: { id }, query: to.query})
                            }
                        })
                    }
                });
            })
        })
    }
}
</script>

<style scoped>
.border {
	border: 1px solid #f1f1f1;
    border-radius: 8px;
}
</style>